import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Search, Lightbulb, Rocket, RefreshCw } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { aboutContent } from '@/data/mock';
import Seo from '@/components/Seo';

const processIcons = [Search, Lightbulb, Rocket, RefreshCw];

export default function AboutPage() {
  return (
    <Layout>
      <Seo
        title="About yBro"
        description="Learn about yBro and how we help UK, US, and India teams improve visibility, manage reviews, and grow local search."
        path="/about"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-ybro-light/50 to-white py-24 lg:py-32">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-ybro-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">About Us</span>
            <h1 className="text-4xl lg:text-5xl font-bold font-heading text-slate-900 mb-8">
              {aboutContent.title}
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-6 font-body">
              {aboutContent.mainText}
            </p>
            <p className="text-lg text-slate-500 leading-relaxed font-body">
              {aboutContent.secondaryText}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-8">
                Built for UK, US, and India businesses of all sizes
              </h2>
              <div className="space-y-4">
                {aboutContent.benefits.map((benefit, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-4 p-5 rounded-xl bg-ybro-light/30 hover:bg-ybro-light border border-ybro-light hover:border-ybro-primary/30 transition-all duration-300"
                  >
                    <CheckCircle2 className="w-6 h-6 text-ybro-primary flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 leading-relaxed font-body">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats/Visual */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-6">
                <div className="p-8 rounded-2xl bg-ybro-light border border-ybro-primary/20">
                  <p className="text-3xl sm:text-4xl font-bold font-heading text-ybro-primary mb-2 leading-tight">100%</p>
                  <p className="text-slate-600 font-body">DMCC Compliant Processes</p>
                </div>
                <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
                  <p className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 mb-2 leading-tight tracking-tight">UK • US • India</p>
                  <p className="text-slate-600 font-body">Teams & Coverage</p>
                </div>
                <div className="p-8 rounded-2xl bg-slate-50 border border-slate-200">
                  <p className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-2 leading-tight">SME</p>
                  <p className="text-slate-600 font-body">to Enterprise Scale</p>
                </div>
                <div className="p-8 rounded-2xl bg-ybro-light border border-ybro-dark/20">
                  <p className="text-3xl sm:text-4xl font-bold font-heading text-ybro-dark mb-2 leading-tight">24/7</p>
                  <p className="text-slate-600 font-body">GBP Monitoring</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 lg:py-32 bg-ybro-light/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">Our Process</span>
            <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-6">
              How we work with you
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {aboutContent.process.map((item, index) => {
              const IconComponent = processIcons[index];
              return (
                <div key={index} className="relative">
                  {/* Connector line */}
                  {index < aboutContent.process.length - 1 && (
                    <div className="hidden lg:block absolute top-12 left-full w-full h-px bg-ybro-primary/30 -translate-y-1/2 z-0" />
                  )}
                  
                  <div className="relative p-8 rounded-2xl bg-white border border-slate-200 hover:border-ybro-primary/40 hover:shadow-lg transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-ybro-light flex items-center justify-center mb-6">
                      {IconComponent && <IconComponent className="w-6 h-6 text-ybro-primary" />}
                    </div>
                    <div className="text-sm font-semibold font-body text-ybro-primary mb-2">Step {index + 1}</div>
                    <h3 className="text-xl font-bold font-heading text-slate-900 mb-3">{item.step}</h3>
                    <p className="text-slate-500 font-body">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-6">
            Ready to work together?
          </h2>
          <p className="text-lg text-slate-500 mb-10 max-w-2xl mx-auto font-body">
            Let's discuss how yBro can help your business dominate local search while staying fully compliant.
          </p>
          <Button 
            asChild
            size="lg"
            className="bg-ybro-primary hover:bg-ybro-dark text-white shadow-xl shadow-ybro-primary/25 hover:shadow-ybro-primary/40 transition-all duration-300 text-base px-8 py-6 h-auto font-body"
          >
            <Link to="/contact" className="flex items-center gap-2">
              Get in Touch
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
}
